<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
// Aceleres — carteirização do gestor.
//
// Antes esta rota renderizava um <iframe src="/funil">: o item de menu, a rota
// e o gate de administrador já existiam, mas a tela nunca foi feita e mostrava
// o funil no lugar. Agora é a lista real de carteiras.
//
// Fonte de dados: só o Zoraxy. A carteira mora em
// contacts.additional_attributes.carteira_owner_id, escopada por account_id.
// Nenhuma chamada ao Aceleres — o espelho para lá é assíncrono e sai do
// backend, igual ao fluxo dos 3 pontinhos.
//
// Layout mestre-detalhe: a lista de carteiras fica sempre visível à esquerda
// para o gestor comparar volumes enquanto navega, em vez de ir e voltar.
import { computed, nextTick, onMounted, ref } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import PortfolioAPI from 'dashboard/api/aceleres/portfolio';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { indicadoresDeCanais } from 'dashboard/helper/aceleresCanalIndicadores';

const UNASSIGNED = 'unassigned';
const isMobileHeader = useMediaQuery('(hover: none) and (pointer: coarse)');

const isLoading = ref(true);
const hasError = ref(false);
const carteiras = ref([]);
const unassigned = ref({ owner_id: UNASSIGNED, contacts_count: 0 });
const totals = ref({
  contacts: 0,
  owned: 0,
  unowned: 0,
  sellers_with_portfolio: 0,
});

const selectedOwner = ref(null);
const contacts = ref([]);
const contactsMeta = ref({ page: 1, per_page: 50, total: 0 });
const isLoadingContacts = ref(false);
const ownerFilter = ref('');
const search = ref('');
const selectedContactIds = ref([]);
const transferTargetId = ref('');
const isSaving = ref(false);
const isTargetPickerOpen = ref(false);
const targetSearch = ref('');
const targetSearchInput = ref(null);

// Destino da transferência: qualquer MEMBRO da conta, gestor incluído.
//
// Antes esta lista era `owner_role === 'agent'`, e por isso não havia como
// entregar uma carteira a um gestor. Isso contrariava a regra do produto —
// designação EXPLÍCITA (transferir, assumir, default_assignee) vale para
// qualquer perfil; o que é restrito a quem atende é a fonte IMPLÍCITA
// (criador da conexão, carteira herdada por importação, rodízio). Contrariava
// também os vizinhos: `AceleresConversationTransferRequest
// .eligible_target_assignees_for` aceita administrador de propósito, o
// backend desta mesma tela nunca olhou o papel, e a coluna da esquerda já
// mostra gestor com carteira. Dava para TIRAR a carteira de um gestor e nunca
// para devolver.
//
// O corte que importa é `owner_role` presente: dono que saiu da empresa vem
// com papel nulo (a linha "(fora da conta)") e o backend recusaria com 404,
// porque procura o destino dentro de `Current.account.users`.
const targetOptions = computed(() =>
  carteiras.value.filter(row => row.owner_role)
);

// O destino da transferência era um <select> nativo: sem foto (o elemento não
// aceita imagem) e sem busca, com 60+ vendedores homônimos numa lista rolante.
// A busca aqui cobre e-mail e ID justamente porque é isso que separa dois
// "Angelo Vendedor" — o nome, sozinho, não separa.
const filteredTargetOptions = computed(() => {
  const term = targetSearch.value.trim().toLowerCase();
  if (!term) return targetOptions.value;
  return targetOptions.value.filter(
    row =>
      row.owner_name.toLowerCase().includes(term) ||
      (row.owner_email || '').toLowerCase().includes(term) ||
      String(row.owner_id) === term
  );
});

const transferTarget = computed(() =>
  targetOptions.value.find(
    row => row.owner_id === Number(transferTargetId.value)
  )
);

const closeTargetPicker = () => {
  isTargetPickerOpen.value = false;
  targetSearch.value = '';
};

const toggleTargetPicker = async () => {
  if (isTargetPickerOpen.value) {
    closeTargetPicker();
    return;
  }
  isTargetPickerOpen.value = true;
  await nextTick();
  targetSearchInput.value?.focus();
};

const selectTarget = row => {
  transferTargetId.value = row.owner_id;
  closeTargetPicker();
};

// A maior carteira define a escala da barra — comparar entre vendedores é o
// que o gestor faz aqui, então a referência é o topo da lista, não o total.
const largestPortfolio = computed(() =>
  Math.max(1, ...carteiras.value.map(row => row.contacts_count))
);

const filteredCarteiras = computed(() => {
  const term = ownerFilter.value.trim().toLowerCase();
  if (!term) return carteiras.value;
  return carteiras.value.filter(
    row =>
      row.owner_name.toLowerCase().includes(term) ||
      (row.owner_email || '').toLowerCase().includes(term) ||
      String(row.owner_id) === term
  );
});

// Homônimos são comuns aqui (usuários de teste, "Gestor Teste SS" x6): o nome
// sozinho não identifica ninguém. Quem repete nome ganha o e-mail em destaque.
const duplicatedNames = computed(() => {
  const seen = new Map();
  carteiras.value.forEach(row => {
    const key = (row.owner_name || '').trim().toLowerCase();
    seen.set(key, (seen.get(key) || 0) + 1);
  });
  return new Set(
    [...seen].filter(([, total]) => total > 1).map(([key]) => key)
  );
});

const isDuplicatedName = row =>
  duplicatedNames.value.has((row.owner_name || '').trim().toLowerCase());

const selectedRow = computed(() =>
  carteiras.value.find(row => row.owner_id === selectedOwner.value)
);

const selectedOwnerLabel = computed(() => {
  if (selectedOwner.value === UNASSIGNED) return 'Não Carteirizados';
  return selectedRow.value?.owner_name || '';
});

const allOnPageSelected = computed(
  () =>
    contacts.value.length > 0 &&
    contacts.value.every(contact => selectedContactIds.value.includes(contact.id))
);

const loadIndex = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await PortfolioAPI.index();
    carteiras.value = data.payload.carteiras;
    unassigned.value = data.payload.unassigned;
    totals.value = data.payload.totals;
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const loadContacts = async (page = 1) => {
  if (selectedOwner.value === null) return;
  isLoadingContacts.value = true;
  try {
    const { data } = await PortfolioAPI.contacts({
      ownerId: selectedOwner.value,
      page,
      q: search.value,
    });
    contacts.value = data.payload.contacts;
    contactsMeta.value = data.payload.meta;
  } catch (error) {
    useAlert('Não foi possível carregar os contatos desta carteira');
  } finally {
    isLoadingContacts.value = false;
  }
};

const scrollToMobilePanel = async selector => {
  if (!window.matchMedia('(max-width: 767px)').matches) return;
  await nextTick();
  document.querySelector(selector)?.scrollIntoView({ block: 'start' });
};

const openOwner = ownerId => {
  clearSelection();
  selectedOwner.value = ownerId;
  search.value = '';
  loadContacts(1);
  scrollToMobilePanel('.crt-mobile-contacts');
};

// Por onde o contato fala. Mesmo icone e mesma cor do cartao da lista de
// conversas: o gestor reconhece o canal aqui sem reaprender um desenho novo.
// Contato sem canal nenhum (lead importado que nunca falou) simplesmente nao
// ganha icone -- inventar um seria pior do que nao mostrar.
const canaisDoContato = contact => indicadoresDeCanais(contact.channel_types);

const toggleContact = id => {
  const index = selectedContactIds.value.indexOf(id);
  if (index === -1) selectedContactIds.value.push(id);
  else selectedContactIds.value.splice(index, 1);
};

const toggleAll = () => {
  const pageIds = contacts.value.map(contact => contact.id);
  selectedContactIds.value = allOnPageSelected.value
    ? selectedContactIds.value.filter(id => !pageIds.includes(id))
    : [...new Set([...selectedContactIds.value, ...pageIds])];
};

const clearSelection = () => {
  selectedContactIds.value = [];
  transferTargetId.value = '';
  closeTargetPicker();
};

const backToPortfolios = () => {
  clearSelection();
  selectedOwner.value = null;
  contacts.value = [];
  scrollToMobilePanel('.crt-mobile-portfolios');
};

// targetOwnerId null = liberar. O backend é idempotente: reatribuir para o
// mesmo dono devolve skipped, não erro.
const applyAssignment = async targetOwnerId => {
  if (!selectedContactIds.value.length) return;
  isSaving.value = true;
  try {
    const { data } = await PortfolioAPI.assign({
      contactIds: selectedContactIds.value,
      targetOwnerId,
    });
    const { applied, skipped } = data.payload;
    clearSelection();
    useAlert(
      `${applied} contato(s) atualizados${skipped ? `, ${skipped} sem alteração` : ''}`
    );
    await Promise.all([loadIndex(), loadContacts(contactsMeta.value.page)]);
    transferTargetId.value = '';
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível alterar a carteirização'
    );
  } finally {
    isSaving.value = false;
  }
};

const transfer = () => {
  if (!transferTargetId.value) return;
  applyAssignment(Number(transferTargetId.value));
};

const release = () => applyAssignment(null);

const pageStart = computed(
  () => (contactsMeta.value.page - 1) * contactsMeta.value.per_page + 1
);
const pageEnd = computed(() =>
  Math.min(
    contactsMeta.value.page * contactsMeta.value.per_page,
    contactsMeta.value.total
  )
);
const hasNextPage = computed(
  () =>
    contactsMeta.value.page * contactsMeta.value.per_page <
    contactsMeta.value.total
);

onMounted(loadIndex);
</script>

<template>
  <section
    class="flex flex-col flex-1 min-w-0 min-h-0 overflow-y-auto bg-n-background md:overflow-visible lg:overflow-hidden"
  >
    <header
      class="aceleres-carteirizacao-page__header acl-mobile-account-menu-host flex flex-shrink-0 items-center justify-between gap-4 px-6 py-4 transition-[padding] duration-200 ease-out max-md:px-4"
      :class="
        isMobileHeader
          ? 'crt-mobile-header relative !grid grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] !items-center !gap-2 !px-3 !py-3'
          : ''
      "
    >
      <span v-if="isMobileHeader" aria-hidden="true" />
      <div
        class="carteirizacao-page__header-content min-w-0"
        :class="isMobileHeader ? 'w-full text-center' : 'text-left'"
      >
        <h1
          class="carteirizacao-page__title truncate text-xl font-bold text-n-slate-12 max-md:flex-1 max-md:pr-[52px] max-md:text-center max-md:text-[25px] max-md:leading-7"
        >
          Carteirização
        </h1>
        <p
          class="carteirizacao-page__subtitle mt-1 text-sm text-n-slate-11 max-md:text-center"
        >
          Dono do lead por vendedor. Transferir o atendimento não move a
          carteira.
        </p>
      </div>
      <Button
        variant="outline"
        color="slate"
        size="sm"
        icon="i-lucide-refresh-cw"
        label="Recarregar"
        aria-label="Recarregar carteirização"
        class="!h-8 !px-3"
        :class="
          isMobileHeader
            ? 'crt-mobile-reload !absolute !left-auto !right-5 !top-3 !w-10 !h-10 !p-0 [&>.truncate]:hidden'
            : ''
        "
        @click="loadIndex"
      />
    </header>

    <!-- Resumo -->
    <div
      v-if="!isLoading && !hasError"
      class="crt-mobile-summary grid flex-shrink-0 grid-cols-2 gap-2 px-3 py-3 md:gap-3 md:px-6 md:py-4 lg:grid-cols-4"
    >
      <div class="p-3 rounded-xl bg-n-solid-2 border border-n-weak">
        <div class="flex items-center gap-2 text-n-slate-11">
          <span class="i-lucide-users size-4" />
          <span class="text-xs font-medium">Contatos</span>
        </div>
        <p class="m-0 mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
          {{ totals.contacts }}
        </p>
      </div>
      <div class="p-3 rounded-xl bg-n-solid-2 border border-n-weak">
        <div class="flex items-center gap-2 text-n-teal-11">
          <span class="i-lucide-user-check size-4" />
          <span class="text-xs font-medium">Carteirizados</span>
        </div>
        <p class="m-0 mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
          {{ totals.owned }}
        </p>
      </div>
      <div class="p-3 rounded-xl bg-n-solid-2 border border-n-weak">
        <div class="flex items-center gap-2 text-n-amber-11">
          <span class="i-lucide-user-x size-4" />
          <span class="text-xs font-medium">Sem dono</span>
        </div>
        <p class="m-0 mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
          {{ totals.unowned }}
        </p>
      </div>
      <div class="p-3 rounded-xl bg-n-solid-2 border border-n-weak">
        <div class="flex items-center gap-2 text-n-slate-11">
          <span class="i-lucide-briefcase size-4" />
          <span class="text-xs font-medium">Com carteira</span>
        </div>
        <p class="m-0 mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
          {{ totals.sellers_with_portfolio }}
        </p>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="flex items-center gap-3 px-3 text-sm text-n-slate-11 md:px-6"
    >
      <span class="i-lucide-loader-circle size-5 animate-spin" />
      Carregando carteiras...
    </div>

    <div v-else-if="hasError" class="px-3 md:px-6">
      <div class="flex flex-col items-center gap-3 py-12 text-center">
        <span class="i-lucide-triangle-alert size-8 text-n-amber-11" />
        <p class="m-0 text-sm text-n-slate-11">
          Não foi possível carregar a carteirização.
        </p>
        <Button
          variant="outline"
          color="slate"
          size="sm"
          label="Tentar de novo"
          @click="loadIndex"
        />
      </div>
    </div>

    <!-- Mestre-detalhe -->
    <div
      v-else
      class="crt-mobile-content flex flex-col flex-none min-h-fit gap-3 px-3 pb-[calc(5rem+env(safe-area-inset-bottom))] md:flex-1 md:min-h-0 md:gap-4 md:px-6 md:pb-4 lg:flex-row"
    >
      <!-- Lista de carteiras -->
      <aside
        class="crt-mobile-portfolios flex flex-col flex-none min-h-0 border rounded-xl border-n-weak bg-n-solid-1 lg:w-80 xl:w-96 lg:flex-shrink-0"
        :class="selectedOwner !== null ? '!hidden md:!flex' : ''"
      >
        <!--
          `reset-base` nos dois campos de busca: o reset de formulario do
          Chatwoot (`input[type]:not(...)` em _base.scss) tem especificidade
          muito maior que `.pl-8` e reimpoe `padding: .5rem .75rem`, entao o
          espaco reservado para a lupa sumia e o icone caia por cima do
          placeholder. Os outros campos de busca do Chatwoot escapam por
          omitirem o atributo `type` -- aqui ele existe (`type="search"`), que
          e o que faz o seletor casar.
        -->
        <div class="flex-shrink-0 p-2 border-b border-n-weak">
          <div class="relative">
            <span
              class="absolute -translate-y-1/2 i-lucide-search size-4 left-2.5 top-1/2 text-n-slate-10"
            />
            <input
              v-model="ownerFilter"
              type="search"
              placeholder="Filtrar vendedor, e-mail ou ID"
              class="reset-base w-full py-2 pl-8 pr-3 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
            />
          </div>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto">
          <button
            type="button"
            class="flex items-center w-full gap-3 px-3 py-3 text-left border-b border-n-weak hover:bg-n-alpha-1"
            :class="selectedOwner === UNASSIGNED ? 'bg-n-alpha-2' : ''"
            @click="openOwner(UNASSIGNED)"
          >
            <span
              class="flex items-center justify-center rounded-full size-8 bg-n-amber-3 text-n-amber-11 flex-shrink-0"
            >
              <span class="i-lucide-user-x size-4" />
            </span>
            <span class="flex-1 min-w-0">
              <span class="block text-sm font-medium truncate text-n-slate-12">
                Não Carteirizados
              </span>
              <span class="block text-xs text-n-slate-10">
                sem dono definido
              </span>
            </span>
            <span class="text-sm font-semibold tabular-nums text-n-slate-11">
              {{ unassigned.contacts_count }}
            </span>
          </button>

          <button
            v-for="row in filteredCarteiras"
            :key="row.owner_id"
            type="button"
            class="flex items-center w-full gap-3 px-3 py-3 text-left border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
            :class="selectedOwner === row.owner_id ? 'bg-n-alpha-2' : ''"
            @click="openOwner(row.owner_id)"
          >
            <Avatar
              :name="row.owner_name"
              :src="row.owner_thumbnail"
              :size="32"
              rounded-full
            />
            <span class="flex-1 min-w-0">
              <span class="flex items-center gap-1.5">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ row.owner_name }}
                </span>
                <span
                  v-if="row.owner_role === 'administrator'"
                  class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-slate-3 text-n-slate-11 flex-shrink-0"
                >
                  gestor
                </span>
                <span
                  v-if="isDuplicatedName(row)"
                  class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-amber-3 text-n-amber-11 flex-shrink-0"
                  title="Existe outro usuário com este mesmo nome nesta conta"
                >
                  nome repetido
                </span>
              </span>
              <span
                class="block text-xs truncate"
                :class="
                  isDuplicatedName(row) ? 'text-n-amber-11' : 'text-n-slate-10'
                "
              >
                {{ row.owner_email || `#${row.owner_id}` }}
              </span>
              <span class="block h-1 mt-1.5 rounded-full bg-n-slate-3">
                <span
                  class="block h-1 rounded-full bg-n-brand"
                  :style="{
                    width: `${Math.round((row.contacts_count / largestPortfolio) * 100)}%`,
                  }"
                />
              </span>
            </span>
            <span class="text-sm font-semibold tabular-nums text-n-slate-11">
              {{ row.contacts_count }}
            </span>
          </button>

          <p
            v-if="!filteredCarteiras.length"
            class="px-3 py-6 m-0 text-sm text-center text-n-slate-10"
          >
            Nenhum vendedor com esse nome.
          </p>
        </div>
      </aside>

      <!-- Contatos da carteira -->
      <div
        class="crt-mobile-contacts flex flex-col flex-none min-w-0 min-h-fit border rounded-xl border-n-weak bg-n-solid-1 md:flex-1 md:min-h-0"
        :class="selectedOwner === null ? '!hidden md:!flex' : ''"
      >
        <div
          v-if="selectedOwner === null"
          class="flex flex-col items-center justify-center flex-1 gap-3 px-6 py-16 text-center"
        >
          <span class="i-lucide-folder-kanban size-10 text-n-slate-10" />
          <p class="m-0 text-sm font-medium text-n-slate-12">
            Escolha uma carteira
          </p>
          <p class="m-0 max-w-sm text-sm text-n-slate-10">
            Selecione um vendedor à esquerda para ver os contatos dele,
            transferir ou liberar.
          </p>
        </div>

        <template v-else>
          <div
            class="crt-mobile-contacts-header flex flex-wrap items-center flex-shrink-0 gap-3 px-3 py-3 border-b border-n-weak md:px-4"
          >
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-arrow-left"
              label="Voltar às carteiras"
              class="w-full md:!hidden"
              @click="backToPortfolios"
            />
            <Avatar
              v-if="selectedOwner !== UNASSIGNED"
              :name="selectedOwnerLabel"
              :src="selectedRow?.owner_thumbnail"
              :size="28"
              rounded-full
            />
            <div class="min-w-0">
              <h2 class="m-0 text-sm font-semibold truncate text-n-slate-12">
                {{ selectedOwnerLabel }}
              </h2>
              <p class="m-0 text-xs truncate text-n-slate-10">
                <template v-if="selectedRow">
                  {{ selectedRow.owner_email || `#${selectedRow.owner_id}` }} ·
                </template>
                {{ contactsMeta.total }} contato(s)
              </p>
            </div>
            <span class="flex-1" />
            <div class="relative w-full md:w-auto">
              <span
                class="absolute -translate-y-1/2 i-lucide-search size-4 left-2.5 top-1/2 text-n-slate-10"
              />
              <input
                v-model="search"
                type="search"
                placeholder="Buscar nome ou telefone"
                class="reset-base py-2 pl-8 pr-3 text-sm border rounded-lg w-full md:w-56 border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
                @keyup.enter="loadContacts(1)"
              />
            </div>
            <Button
              variant="outline"
              color="slate"
              size="sm"
              label="Buscar"
              class="w-full md:w-auto"
              @click="loadContacts(1)"
            />
          </div>

          <div
            v-if="isLoadingContacts"
            class="flex items-center gap-2 px-4 py-6 text-sm text-n-slate-11"
          >
            <span class="i-lucide-loader-circle size-4 animate-spin" />
            Carregando contatos...
          </div>

          <template v-else>
            <div
              v-if="contacts.length"
              class="flex items-center flex-shrink-0 gap-3 px-4 py-2 border-b border-n-weak bg-n-alpha-1"
            >
              <input
                type="checkbox"
                :checked="allOnPageSelected"
                class="accent-n-brand"
                @change="toggleAll"
              />
              <span class="text-xs text-n-slate-10">
                Selecionar os {{ contacts.length }} desta página
              </span>
            </div>

            <div class="flex-1 min-h-0 overflow-y-auto">
              <label
                v-for="contact in contacts"
                :key="contact.id"
                class="flex items-center min-h-12 gap-3 px-3 py-2.5 border-b border-n-weak last:border-b-0 cursor-pointer hover:bg-n-alpha-1 md:px-4"
                :class="
                  selectedContactIds.includes(contact.id) ? 'bg-n-alpha-2' : ''
                "
              >
                <input
                  type="checkbox"
                  :checked="selectedContactIds.includes(contact.id)"
                  class="accent-n-brand"
                  @change="toggleContact(contact.id)"
                />
                <Avatar
                  :name="contact.name || '?'"
                  :src="contact.thumbnail"
                  :size="28"
                  rounded-full
                />
                <span class="flex-1 min-w-0">
                  <span class="block text-sm truncate text-n-slate-12">
                    {{ contact.name }}
                  </span>
                  <span class="flex items-center gap-1.5 min-w-0">
                    <span
                      v-for="canal in canaisDoContato(contact)"
                      :key="canal.key"
                      v-tooltip.top="canal.label"
                      class="size-3.5 shrink-0"
                      :class="[canal.icon, canal.color]"
                      role="img"
                      :aria-label="canal.label"
                    />
                    <span class="text-xs truncate text-n-slate-10">
                      {{ contact.phone_number || 'sem telefone' }}
                    </span>
                  </span>
                </span>
              </label>

              <div
                v-if="!contacts.length"
                class="flex flex-col items-center gap-2 px-6 py-12 text-center"
              >
                <span class="i-lucide-inbox size-8 text-n-slate-10" />
                <p class="m-0 text-sm text-n-slate-10">
                  Nenhum contato nesta carteira.
                </p>
              </div>
            </div>

            <div
              v-if="contactsMeta.total > contactsMeta.per_page"
              class="flex flex-wrap items-center flex-shrink-0 gap-2 px-3 py-2 border-t border-n-weak md:flex-nowrap md:gap-3 md:px-4"
            >
              <span class="text-xs text-n-slate-10">
                {{ pageStart }}–{{ pageEnd }} de {{ contactsMeta.total }}
              </span>
              <span class="flex-1" />
              <Button
                variant="outline"
                color="slate"
                size="xs"
                label="Anterior"
                :disabled="contactsMeta.page <= 1"
                @click="loadContacts(contactsMeta.page - 1)"
              />
              <Button
                variant="outline"
                color="slate"
                size="xs"
                label="Próxima"
                :disabled="!hasNextPage"
                @click="loadContacts(contactsMeta.page + 1)"
              />
            </div>
          </template>

          <!-- Barra de ação: só aparece com seleção, para não competir com a lista -->
          <div
            v-if="selectedContactIds.length"
            class="crt-mobile-actions flex flex-wrap items-center flex-shrink-0 gap-2 px-3 py-3 border-t border-n-weak bg-n-solid-2 md:px-4"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ selectedContactIds.length }} selecionado(s)
            </span>
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              label="Limpar"
              @click="clearSelection"
            />
            <span class="flex-1" />
            <div
              v-on-clickaway="closeTargetPicker"
              class="relative w-full md:w-auto"
            >
              <button
                type="button"
                class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm border rounded-lg md:w-auto border-n-weak bg-n-background text-n-slate-12 hover:border-n-slate-6 focus:outline-none focus:border-n-brand"
                @click="toggleTargetPicker"
              >
                <Avatar
                  v-if="transferTarget"
                  :name="transferTarget.owner_name"
                  :src="transferTarget.owner_thumbnail"
                  :size="20"
                  rounded-full
                />
                <span class="max-w-48 truncate">
                  {{
                    transferTarget
                      ? transferTarget.owner_name
                      : 'Transferir para...'
                  }}
                </span>
                <span
                  class="size-4 shrink-0 text-n-slate-10"
                  :class="
                    isTargetPickerOpen
                      ? 'i-lucide-chevron-down'
                      : 'i-lucide-chevron-up'
                  "
                />
              </button>

              <!--
                Abre para CIMA: esta barra de ação vive no rodapé do painel, e
                um menu para baixo sairia da tela.
              -->
              <div
                v-if="isTargetPickerOpen"
                class="absolute bottom-full z-50 mb-1 w-full overflow-hidden border shadow-xl md:w-80 rounded-xl border-n-weak bg-n-solid-1 ltr:right-0 rtl:left-0"
              >
                <div class="p-2 border-b border-n-weak">
                  <div class="relative">
                    <span
                      class="absolute -translate-y-1/2 i-lucide-search size-4 left-2.5 top-1/2 text-n-slate-10"
                    />
                    <input
                      ref="targetSearchInput"
                      v-model="targetSearch"
                      type="search"
                      placeholder="Buscar pessoa, e-mail ou ID"
                      class="reset-base w-full py-2 pl-8 pr-3 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
                      @keyup.esc="closeTargetPicker"
                    />
                  </div>
                </div>

                <div class="overflow-y-auto max-h-72">
                  <button
                    v-for="s in filteredTargetOptions"
                    :key="s.owner_id"
                    type="button"
                    class="flex items-center w-full gap-2 px-3 py-2 text-left border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
                    :class="
                      Number(transferTargetId) === s.owner_id
                        ? 'bg-n-alpha-2'
                        : ''
                    "
                    @click="selectTarget(s)"
                  >
                    <Avatar
                      :name="s.owner_name"
                      :src="s.owner_thumbnail"
                      :size="24"
                      rounded-full
                    />
                    <span class="flex-1 min-w-0">
                      <span class="flex items-center gap-1.5 min-w-0">
                        <span
                          class="text-sm font-medium truncate text-n-slate-12"
                        >
                          {{ s.owner_name }}
                        </span>
                        <span
                          v-if="s.owner_role === 'administrator'"
                          class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-slate-3 text-n-slate-11 flex-shrink-0"
                        >
                          gestor
                        </span>
                      </span>
                      <span
                        class="block text-xs truncate"
                        :class="
                          isDuplicatedName(s)
                            ? 'text-n-amber-11'
                            : 'text-n-slate-10'
                        "
                      >
                        {{ s.owner_email || `#${s.owner_id}` }}
                      </span>
                    </span>
                    <span
                      class="text-xs tabular-nums text-n-slate-10 shrink-0"
                      title="Contatos já nesta carteira"
                    >
                      {{ s.contacts_count }}
                    </span>
                  </button>

                  <p
                    v-if="!filteredTargetOptions.length"
                    class="px-3 py-6 m-0 text-sm text-center text-n-slate-10"
                  >
                    Ninguém encontrado.
                  </p>
                </div>
              </div>
            </div>
            <Button
              variant="solid"
              color="blue"
              size="sm"
              label="Transferir"
              :is-loading="isSaving"
              :disabled="!transferTargetId || isSaving"
              class="flex-1 md:flex-none"
              @click="transfer"
            />
            <Button
              v-if="selectedOwner !== UNASSIGNED"
              variant="outline"
              color="ruby"
              size="sm"
              label="Liberar"
              :disabled="isSaving"
              class="flex-1 md:flex-none"
              @click="release"
            />
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped>
.aceleres-carteirizacao-page__header {
  box-shadow: 0 1px 0 rgba(226, 232, 240, 0.9);
}
</style>
